import { pick, unwrapData, unwrapList } from '../../utils/moduleHelpers';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toList = (payload) => {
  const data = unwrapData(payload) ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  return unwrapList({ data }) || [];
};

export const formatMoney = (value) => {
  const n = toNum(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatEvaluationPeriod = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? `${value} days` : '';
  }
  const text = String(value).trim();
  if (!text) return '';
  if (/^\d+(\.\d+)?$/.test(text)) {
    const n = Number(text);
    return n > 0 ? `${n} days` : '';
  }
  return text;
};

export const parseTierLevels = (partnersRes) => {
  const list = toList(partnersRes);
  const mapped = list.map((item, index) => {
    // Prefer explicit LevelNumber; avoid treating affiliateLevelId as sequence
    const explicitLevel =
      item.LevelNumber ?? item.levelNumber ?? item.level ?? item.Level ?? null;
    const levelNumber = explicitLevel != null ? toNum(explicitLevel) : index + 1;
    return {
      id: item.affiliateLevelId ?? item.affiliateTypeId ?? item.id ?? levelNumber,
      levelNumber,
      name: item.levelName || item.typeName || item.name || `Level ${levelNumber}`,
      points: toNum(item.levelPoints ?? item.points ?? item.targetPoints ?? item.requiredPoints),
      commissionPerLot: toNum(
        item.maximumCommission ?? item.commissionPerLot ?? item.commissionPercentage ?? item.earning
      ),
      description: item.description || item.details || '',
      minimumLots: toNum(item.minimumLots ?? item.minLots),
      maximumLots: toNum(item.maximumLots ?? item.maxLots),
      raw: item,
    };
  });

  // Order by level number when present, then by points (starter → higher tiers)
  return mapped.sort((a, b) => {
    if (a.levelNumber !== b.levelNumber) return a.levelNumber - b.levelNumber;
    return a.points - b.points;
  });
};

export const parseUserTier = (levelRes, profileRes) => {
  const level = unwrapData(levelRes) ?? levelRes ?? {};
  const profile = unwrapData(profileRes) ?? profileRes ?? {};

  const currentLevel = toNum(level.currentLevel ?? level.CurrentLevel ?? level.level);
  const currentLevelName =
    level.currentLevelName || level.CurrentLevelName || level.levelName || profile.currentLevelName || '';
  const nextLevelNameRaw = String(level.nextLevelName || level.NextLevelName || '').trim();
  const nextLevelName =
    !nextLevelNameRaw || /^max\s*level$/i.test(nextLevelNameRaw) ? '' : nextLevelNameRaw;

  // Prefer currentPoints (AffiliateWallet.TotalLoyaltyPoints); currentVolume is backward compat
  const currentPoints = toNum(
    level.currentPoints ??
      level.CurrentPoints ??
      level.currentVolume ??
      level.CurrentVolume ??
      level.points
  );

  // Next tier target: nextLevelPoints / currentVolumeTarget
  const targetPoints = toNum(
    level.nextLevelPoints ??
      level.NextLevelPoints ??
      level.currentVolumeTarget ??
      level.CurrentVolumeTarget ??
      level.targetPoints ??
      level.nextLevelTarget
  );

  const pointsRemainingApi = level.pointsRemaining ?? level.PointsRemaining;
  const pointsToNext =
    pointsRemainingApi != null && pointsRemainingApi !== ''
      ? Math.max(0, toNum(pointsRemainingApi))
      : Math.max(0, targetPoints - currentPoints);

  const progressPct =
    targetPoints > 0 ? Math.min(100, Math.round((currentPoints / targetPoints) * 100)) : 0;

  const createdDate =
    pick(profile, 'createdDateTime', 'CreatedDateTime', 'createdDate', 'CreatedDate', 'registrationDate') ||
    pick(level, 'startDate', 'StartDate', 'createdDate', 'accountCreatedDate') ||
    '';

  const startDate = pick(level, 'startDate', 'StartDate', 'levelStartDate') || createdDate;
  const lastUpgrade = pick(level, 'lastUpgradeDate', 'LastUpgradeDate', 'lastUpgrade') || startDate;
  const evaluationPeriod = formatEvaluationPeriod(
    pick(
      level,
      'evaluationPeriod',
      'EvaluationPeriod',
      'performanceEvaluationPeriod',
      'PerformanceEvaluationPeriod',
      'evaluationPeriodDays',
      'EvaluationPeriodDays',
      'evaluationDays',
      'periodDays'
    )
  );
  const daysLeftRaw = level.daysLeft ?? level.daysRemaining ?? level.DaysLeft ?? level.daysLeftInPeriod;
  const daysLeft =
    daysLeftRaw == null || daysLeftRaw === '' ? null : toNum(daysLeftRaw);
  const periodPoints = toNum(level.periodPoints ?? level.evaluationPoints ?? level.pointsInPeriod ?? currentPoints);

  const commissionPerLot = toNum(
    level.maximumCommission ?? level.commissionPerLot ?? level.commissionPercentage
  );

  return {
    currentLevel,
    currentLevelName: currentLevelName || (currentLevel ? `Level ${currentLevel}` : '—'),
    nextLevelName,
    currentPoints,
    targetPoints,
    nextLevelPoints: targetPoints,
    pointsToNext,
    progressPct,
    createdDate,
    startDate,
    lastUpgrade,
    evaluationPeriod,
    daysLeft,
    periodPoints,
    commissionPerLot,
    activeClients: toNum(level.activeClients ?? level.ActiveClients),
    minimumLots: toNum(level.minimumLots),
    maximumLots: toNum(level.maximumLots),
    commissionPercentage: toNum(level.commissionPercentage),
    raw: level,
    profile,
  };
};

/**
 * History rows for legacy GetUserLevels consumers.
 * Prefer mapUpgradeHistory / parseAccountTierProgress for My Progress.
 */
export const buildTierHistory = (userTier, tiers) => {
  const apiHistory =
    unwrapList({ data: userTier.raw?.levelHistory }) ||
    unwrapList({ data: userTier.raw?.tierHistory }) ||
    unwrapList({ data: userTier.raw?.history }) ||
    [];

  const mapped = apiHistory.map((row, index) => ({
    id: row.id || `api-${index}`,
    fromLevel: row.fromLevelName || row.fromLevel || row.FromLevel || '—',
    startDate: formatDate(row.startDate || row.StartDate || row.fromDate),
    lastUpgrade: formatDate(row.lastUpgrade || row.LastUpgradeDate || row.upgradeDate),
    toLevel: row.toLevelName || row.toLevel || row.ToLevel || '—',
    evaluationPeriod: formatEvaluationPeriod(
      row.evaluationPeriod ||
        row.performanceEvaluationPeriod ||
        row.EvaluationPeriod ||
        userTier.evaluationPeriod
    ) || '—',
  }));

  if (mapped.length) return mapped;

  const firstTier = tiers[0];
  const currentName = userTier.currentLevelName || firstTier?.name || 'Starter';
  const created = formatDate(userTier.createdDate || userTier.startDate);

  return [
    {
      id: 'account-created',
      fromLevel: '—',
      startDate: created,
      lastUpgrade: created,
      toLevel: firstTier?.name || currentName,
      evaluationPeriod: userTier.evaluationPeriod || '—',
    },
  ];
};

/**
 * Parse GET /Affiliate/GetAccountTierProgress for My Progress.
 * One payload covers banner, tracker, ring, metrics, timeline, and history.
 */
export const parseAccountTierProgress = (payload) => {
  const data = unwrapData(payload) ?? payload ?? {};

  const currentPoints = toNum(data.currentPoints);
  const nextLevelPoints = toNum(data.nextLevelPoints);
  const pointsRemainingRaw = data.pointsRemaining ?? data.PointsRemaining;
  const pointsRemaining =
    pointsRemainingRaw != null && pointsRemainingRaw !== ''
      ? Math.max(0, toNum(pointsRemainingRaw))
      : Math.max(0, nextLevelPoints - currentPoints);

  const progressRaw = data.progressPercent ?? data.ProgressPercent;
  const progressPercent =
    progressRaw != null && progressRaw !== ''
      ? Math.min(100, Math.max(0, Math.round(toNum(progressRaw))))
      : nextLevelPoints > 0
        ? Math.min(100, Math.round((currentPoints / nextLevelPoints) * 100))
        : 0;

  const isMaxTier = Boolean(data.isMaxTier ?? data.IsMaxTier);
  const daysLeftRaw = data.daysLeft ?? data.DaysLeft;
  const daysLeft =
    daysLeftRaw == null || daysLeftRaw === '' ? null : toNum(daysLeftRaw);

  const startDate = pick(data, 'startDate', 'StartDate') || null;
  const endDate = pick(data, 'endDate', 'EndDate') || null;
  const lastUpgradeDate = pick(data, 'lastUpgradeDate', 'LastUpgradeDate') || null;
  const accountCreatedDate =
    pick(data, 'accountCreatedDate', 'AccountCreatedDate') || null;

  const evaluationPeriod =
    startDate && endDate ? `${formatDate(startDate)} – ${formatDate(endDate)}` : null;

  const tiersRaw = Array.isArray(data.tiers)
    ? data.tiers
    : Array.isArray(data.Tiers)
      ? data.Tiers
      : [];

  const tiers = tiersRaw.map((item, index) => {
    const levelPoints = toNum(item.levelPoints ?? item.LevelPoints);
    return {
      id: item.affiliateLevelId ?? item.AffiliateLevelId ?? item.id ?? index + 1,
      affiliateLevelId: item.affiliateLevelId ?? item.AffiliateLevelId ?? item.id,
      levelNumber: index + 1,
      name: item.levelName || item.LevelName || `Level ${index + 1}`,
      points: levelPoints,
      levelPoints,
      commissionPerLot: toNum(item.maximumCommission ?? item.MaximumCommission),
      isCompleted: Boolean(item.isCompleted ?? item.IsCompleted),
      isCurrent: Boolean(item.isCurrent ?? item.IsCurrent),
      raw: item,
    };
  });

  const currentTier =
    tiers.find((t) => t.isCurrent) ||
    tiers.find(
      (t) =>
        String(t.name).toLowerCase() ===
        String(data.currentLevelName || data.CurrentLevelName || '').toLowerCase()
    ) ||
    null;

  const currentIdx = currentTier
    ? tiers.findIndex((t) => t.id === currentTier.id || t.name === currentTier.name)
    : -1;
  const nextTier =
    !isMaxTier && currentIdx >= 0 && currentIdx < tiers.length - 1
      ? tiers[currentIdx + 1]
      : !isMaxTier
        ? tiers.find((t) => !t.isCompleted && !t.isCurrent) || null
        : null;

  const maximumCommission = toNum(
    data.maximumCommission ?? data.MaximumCommission ?? currentTier?.commissionPerLot
  );

  const userTier = {
    currentLevelName:
      data.currentLevelName ||
      data.CurrentLevelName ||
      currentTier?.name ||
      '—',
    maximumCommission,
    commissionPerLot: maximumCommission,
    isMaxTier,
    currentPoints,
    nextLevelPoints,
    targetPoints: nextLevelPoints,
    pointsToNext: pointsRemaining,
    pointsRemaining,
    progressPct: progressPercent,
    progressPercent,
    activeClients: toNum(data.activeClients ?? data.ActiveClients),
    tiersAvailable:
      data.tiersAvailable != null && data.tiersAvailable !== ''
        ? toNum(data.tiersAvailable)
        : tiers.length,
    daysLeft,
    startDate,
    endDate,
    lastUpgrade: lastUpgradeDate,
    lastUpgradeDate,
    createdDate: accountCreatedDate,
    accountCreatedDate,
    evaluationPeriod,
    nextLevelName: isMaxTier ? '' : nextTier?.name || '',
    currentLevel: currentIdx >= 0 ? currentIdx + 1 : 0,
    raw: data,
  };

  const history = mapUpgradeHistory(data.upgradeHistory ?? data.UpgradeHistory);

  return { userTier, tiers, history, currentTier, nextTier };
};

/** Map upgradeHistory from GetAccountTierProgress. Empty → []; never invent rows. */
export const mapUpgradeHistory = (list) => {
  const rows = Array.isArray(list)
    ? list
    : unwrapList({ data: list }) || [];

  return rows.map((row, index) => ({
    id: row.id ?? row.Id ?? `upgrade-${index}`,
    startDate: formatDate(row.startDate ?? row.StartDate),
    message: String(row.message ?? row.Message ?? '').trim(),
    status: row.status ?? row.Status ?? '',
    levelName: row.levelName ?? row.LevelName ?? '',
    evaluatedPoints: row.evaluatedPoints ?? row.EvaluatedPoints,
  }));
};

export const matchCurrentTier = (tiers, userTier) => {
  if (!tiers.length) return null;
  const byFlag = tiers.find((t) => t.isCurrent);
  if (byFlag) return byFlag;
  const byId = tiers.find(
    (t) =>
      t.id === userTier.currentLevel ||
      t.levelNumber === userTier.currentLevel
  );
  if (byId) return byId;
  const byName = tiers.find(
    (t) => String(t.name).toLowerCase() === String(userTier.currentLevelName).toLowerCase()
  );
  return byName || tiers[0];
};

export const getNextTier = (tiers, current, userTier = null) => {
  if (!tiers.length) return null;

  if (userTier?.nextLevelName) {
    const byApiName = tiers.find(
      (t) => String(t.name).toLowerCase() === String(userTier.nextLevelName).toLowerCase()
    );
    if (byApiName) return byApiName;
  }

  if (!current) return tiers.length > 1 ? tiers[1] : null;

  const idx = tiers.findIndex(
    (t) =>
      t.id === current.id ||
      (t.levelNumber === current.levelNumber &&
        String(t.name).toLowerCase() === String(current.name).toLowerCase()) ||
      String(t.name).toLowerCase() === String(current.name).toLowerCase()
  );

  if (idx >= 0 && idx < tiers.length - 1) return tiers[idx + 1];

  // Consecutive LevelNumber (legacy)
  const byNumber = tiers.find((t) => t.levelNumber === current.levelNumber + 1);
  if (byNumber) return byNumber;

  // Next higher points requirement
  const nextByPoints = tiers
    .filter((t) => t.points > (current.points || 0))
    .sort((a, b) => a.points - b.points)[0];
  if (nextByPoints) return nextByPoints;

  // Match API target points to a higher tier
  const target = toNum(userTier?.targetPoints);
  if (target > 0) {
    const byTarget = tiers.find(
      (t) =>
        t.points === target &&
        String(t.name).toLowerCase() !== String(current.name).toLowerCase()
    );
    if (byTarget) return byTarget;
  }

  return null;
};
